import { useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Option, toast } from '@/lib/v2/components';

import { useService } from '@/src/infrastructure/Protocol/useEmblue';
import { atomCreateOrEditIntegrationForm } from '@/src/modules/IntegrationsModule/atoms/integrations';

export const useGroups = () => {
  const service = useService();
  const { t } = useTranslation();
  const [groupsOptions, setGroupsOptions] = useState<Option[]>();
  const [isLoading, setIsLoading] = useState(false);
  const setStateForm = useSetAtom(atomCreateOrEditIntegrationForm);

  const getGroups = useCallback(
    async (groupIds: number[]) => {
      setIsLoading(true);
      const response = await service.getGroups({
        identifier: 'GROUPS_VIEW',
        page: 1,
        pageSize: 1000,
      });
      setIsLoading(false);
      if (response.list) {
        const groupsMap: Option[] = response.list.map((group) => {
          const { id, groupName } = group.group;
          return { id, value: id, name: groupName } as Option;
        });
        setGroupsOptions(groupsMap);
        return groupsMap.filter((group) => groupIds.includes(Number(group.id)));
      } else {
        return [];
      }
    },
    [service]
  );

  const createGroup = useCallback(
    async (name: string) => {
      const response = await service.createGroup(name);
      const OkBody = t('MANAGE_GROUP_MODAL.NOTIFICATION.OkBody');
      let alertBody = t('MANAGE_GROUP_MODAL.NOTIFICATION.KoBody');
      let result = false;

      if (response.groupId) {
        result = true;
      } else if (response.errorByName) {
        alertBody = t('MANAGE_GROUP_MODAL.NOTIFICATION.ExistingBody');
      } else if (response.errorByLimit) {
        alertBody = t('MANAGE_GROUP_MODAL.NOTIFICATION.LimitBody');
      } else {
        alertBody = t('MANAGE_GROUP_MODAL.NOTIFICATION.KoBody');
      }

      toast({
        title: result
          ? t('MANAGE_GROUP_MODAL.NOTIFICATION.OkTitle')
          : t('MANAGE_GROUP_MODAL.NOTIFICATION.AlertTitle'),
        body: result ? OkBody : alertBody,
        variant: result ? 'success' : 'error',
      });

      if (result) {
        const id = response.groupId;
        const newGroup = { id, value: id, name };
        setGroupsOptions((prev) => {
          return [newGroup, ...(prev || [])];
        });
        setStateForm((prev) => ({
          ...prev,
          stepThreeGroups: [...prev.stepThreeGroups, newGroup],
        }));
      }
    },
    [service, setStateForm, t]
  );

  return {
    groupsOptions: groupsOptions || [],
    getGroups,
    createGroup,
    isLoading,
  };
};
